import { Link } from "react-router-dom";
import { EVENTS, categories } from "@/mocks/events";
import SectionHeading from "./SectionHeading";

interface LatestFeedProps {
  active: string;
  onPick: (category: string) => void;
}

export default function LatestFeed({ active, onPick }: LatestFeedProps) {
  const list =
    active === "全部" ? EVENTS : EVENTS.filter((e) => e.category === active);

  return (
    <section
      id="latest"
      className="w-full bg-background-100"
      aria-label="最新事件速览"
    >
      <div className="mx-auto w-full max-w-7xl px-4 py-14 md:px-6 md:py-20">
        <SectionHeading
          icon="ri-flashlight-line"
          kicker="最新速览"
          title="刚刚入库的瓜局"
          desc="我们的编辑持续跟踪热点，把最新发酵的事件按主题归档。切换分类，按你的兴趣逛一逛。"
        />

        {/* 分类切换 */}
        <div className="mt-8 flex flex-wrap items-center justify-center gap-2" role="tablist" aria-label="事件分类筛选">
          {categories.map((cat) => {
            const selected = active === cat.key;
            return (
              <button
                key={cat.key}
                role="tab"
                aria-selected={selected}
                type="button"
                onClick={() => onPick(cat.key)}
                className={`flex items-center gap-1.5 whitespace-nowrap rounded-full px-1 py-1 text-sm transition-all ${
                  selected
                    ? "bg-background-200 text-foreground-900"
                    : "text-foreground-600 hover:bg-background-200/50"
                }`}
              >
                <span
                  className={`flex items-center gap-1.5 rounded-full px-4 py-1.5 transition-colors ${
                    selected
                      ? "bg-primary-500 text-background-50"
                      : "bg-transparent"
                  }`}
                >
                  <i className={`${cat.icon} text-base`} aria-hidden="true"></i>
                  {cat.key}
                  <span className={`text-xs ${selected ? "text-primary-100" : "text-foreground-400"}`}>
                    {cat.key === "全部"
                      ? EVENTS.length
                      : EVENTS.filter((e) => e.category === cat.key).length}
                  </span>
                </span>
              </button>
            );
          })}
        </div>

        {/* 卡片流 */}
        <div className="mt-9 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {list.map((item, idx) => (
            <Link
              key={item.id}
              to={`/story/${item.id}`}
              className="group flex flex-col overflow-hidden rounded-2xl bg-background-50 transition-all hover:-translate-y-1"
              style={{ animationDelay: `${idx * 40}ms` }}
            >
              <div className="relative aspect-[4/3] w-full overflow-hidden">
                <img
                  src={item.cover}
                  alt={`${item.title}封面`}
                  className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                />
                <span className="absolute left-3 top-3 rounded-full bg-background-50/85 px-2.5 py-1 text-[11px] font-semibold text-primary-700 backdrop-blur-sm">
                  {item.category}
                </span>
                <span className="absolute bottom-3 right-3 rounded-full bg-foreground-950/55 px-2.5 py-1 text-[11px] text-background-50 backdrop-blur-sm">
                  {item.published}
                </span>
              </div>
              <div className="flex flex-1 flex-col p-4">
                <h3 className="line-clamp-2 text-base font-bold leading-snug text-foreground-950 group-hover:text-primary-700">
                  {item.title}
                </h3>
                <p className="mt-2 line-clamp-2 flex-1 text-sm leading-relaxed text-foreground-600">
                  {item.excerpt}
                </p>
                <div className="mt-3 flex items-center justify-between border-t border-background-200 pt-3">
                  <span className="flex items-center gap-3 text-xs text-foreground-500">
                    <span className="flex items-center gap-1">
                      <i className="ri-eye-line" aria-hidden="true"></i>
                      {item.views}
                    </span>
                    <span className="flex items-center gap-1">
                      <i className="ri-chat-3-line" aria-hidden="true"></i>
                      {item.comments}
                    </span>
                  </span>
                  <span className="flex items-center gap-1 text-xs font-medium text-primary-600">
                    读懂来龙去脉
                    <i className="ri-arrow-right-line transition-transform group-hover:translate-x-1" aria-hidden="true"></i>
                  </span>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}